import { FC, useRef, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts';
import { IntensityByVehicleResponse } from './types';
import { seriesPrimary, chartChrome } from '../../utils/chartColors';
import ChartErrorState from './ChartErrorState';
import { formatDecimal } from '../../utils/localeFormat';
import './IntensityByVehicleChart.scss';

interface IntensityByVehicleChartProps {
  data: IntensityByVehicleResponse | null;
  isLoading: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

const IntensityByVehicleChart: FC<IntensityByVehicleChartProps> = ({ data, isLoading, error, onRetry }) => {
  const { t, i18n } = useTranslation();
  // optional: formatters fall back to the i18next fallbackLng when absent,
  // which must not become a crash if useTranslation yields no i18n
  const lng = i18n?.language;
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [chartNode, setChartNode] = useState<HTMLDivElement | null>(null);

  const chartCallbackRef = useCallback((node: HTMLDivElement | null) => {
    setChartNode(node);
  }, []);

  useEffect(() => {
    if (!chartNode || !data || data.data.length === 0) {
      return;
    }

    if (!chartInstance.current || chartInstance.current.getDom() !== chartNode) {
      chartInstance.current?.dispose();
      chartInstance.current = echarts.init(chartNode);
    }

    const vehicles = data.data.map((d) =>
      d.vehicleId.length > 15 ? `${d.vehicleId.substring(0, 15)}...` : d.vehicleId
    );
    const values = data.data.map((d) => d.intensityKgPerTonKm);

    const chrome = chartChrome();
    const primary = seriesPrimary();

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          const item = data.data[p.dataIndex];
          return `${item.vehicleId}<br/>${p.marker} ${formatDecimal(Number(p.value), 4, lng)} kg/ton·km`;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        top: '10%',
        bottom: '5%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: vehicles,
        axisLabel: { fontSize: 10, color: chrome.muted, rotate: 30 },
      },
      yAxis: {
        type: 'value',
        name: String(t('cpi.axis.perTonKm')),
        nameTextStyle: { color: chrome.muted, fontSize: 11 },
        axisLabel: { color: chrome.muted, formatter: (v: number) => formatDecimal(v, 3, lng) },
        splitLine: { lineStyle: { type: 'dotted', color: chrome.grid } },
      },
      series: [
        {
          type: 'bar',
          data: values,
          itemStyle: { color: primary },
          barMaxWidth: 40,
          label: {
            show: true,
            position: 'top',
            fontSize: 10,
            formatter: (params: any) => formatDecimal(Number(params.value), 4, lng),
          },
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [data, chartNode, lng, t]);

  useEffect(() => {
    const handleResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  if (error && !isLoading) {
    return (
      <div className="intensity-by-vehicle-chart">
        <h3 className="intensity-by-vehicle-chart__title">{t('cpi.analytics.intensityByVehicle')}</h3>
        <ChartErrorState onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="intensity-by-vehicle-chart intensity-by-vehicle-chart--loading">
        <h3 className="intensity-by-vehicle-chart__title">{t('cpi.analytics.intensityByVehicle')}</h3>
        <div className="intensity-by-vehicle-chart__skeleton" />
      </div>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <div className="intensity-by-vehicle-chart">
        <h3 className="intensity-by-vehicle-chart__title">{t('cpi.analytics.intensityByVehicle')}</h3>
        <p className="chart-no-data">{t('cpi.noDataAvailableForChart')}</p>
      </div>
    );
  }

  return (
    <div className="intensity-by-vehicle-chart">
      <h3 className="intensity-by-vehicle-chart__title">{t('cpi.analytics.intensityByVehicle')}</h3>
      <div ref={chartCallbackRef} style={{ height: '350px', width: '100%' }} />
    </div>
  );
};

export default IntensityByVehicleChart;
